"use client"

import React, { useState, useEffect, useRef } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Camera, Download, FileText, Keyboard, Mic, PlusIcon, TrashIcon, X, ChevronLeft, ChevronRight } from 'lucide-react'
import AddPhotoModal from '@/features/photos/components/add-photo-modal'
import { getPhotosByHomeId, deletePhoto, updatePhoto, Photo } from '@/features/photos/actions'
import { getAllWorkOrders } from '@/features/work-orders/actions'
import { WorkOrder } from '@/features/work-orders/types/work-order.type'
import WorkOrderTimelineCard from './work-order-timeline-card'
import Image from 'next/image'
import Link from 'next/link'
import VideoThumbnail from '@/features/photos/components/video-thumbnail'
import { PdfThumbnail } from '@/features/photos/components/pdf-thumbnail'
import VideoPlayer from '@/features/photos/components/video-player'
import { useSession } from '@/features/auth/context/session.context'
import { UserRole } from '@/shared/constants/user-roles'
import PhotoDescriptionIndicator from '@/features/photos/components/photo-description-indicator'
import { ZoomableImage } from '@/features/photos/components/zoomable-image'
import CameraCaptureModal from '@/features/photos/components/camera-capture-modal'
import PostCapturePopup from '@/features/photos/components/post-capture-popup'
import VoiceRecordingModal from '@/features/photos/components/voice-recording-modal'
import TextInputModal from '@/features/photos/components/text-input-modal'
import { downloadPhotoOrVideo } from '@/features/photos/lib/download-media'
import { uploadMultiplePhotosDirect } from '@/features/photos/upload-client'

const CONFIDENTIAL_TAG = 'Confidential'
const PHOTOS_SHARING_MESSAGE =
  "Thanks for sharing! By posting your photo or video, you're giving ReliefHub the green light to display it across the platform for all users to see and celebrate."

type PhotoTimelineTabProps = {
    homeId: string | number | null
}

interface TimelineDay {
    date: string
    displayDate: string
    photos: Photo[]
    workOrders: WorkOrder[]
}

interface DisplayPhoto {
    id: string
    url: string
    thumbnailUrl: string
    name: string
    takenAt: string | null
    uploadedAt: string
    workOrderId?: string | null
    workOrderNumber?: string | null
}

export default function PhotoTimelineTab({ homeId }: PhotoTimelineTabProps) {
    const { session } = useSession()
    const [isModalOpen, setIsModalOpen] = useState(false)
    const [preSelectedFiles, setPreSelectedFiles] = useState<File[]>([])
    const fileInputRef = useRef<HTMLInputElement>(null)
    const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null)
    const [selectedVideo, setSelectedVideo] = useState<Photo | null>(null)
    // Viewer description edit (for uploaded photos): pending unsaved text; voice/text modals
    const [pendingViewerDescription, setPendingViewerDescription] = useState<string | undefined>(undefined)
    const [isViewerVoiceOpen, setIsViewerVoiceOpen] = useState(false)
    const [isViewerTextOpen, setIsViewerTextOpen] = useState(false)
    const [isViewerTranscribing, setIsViewerTranscribing] = useState(false)
    const [photoTagFilter, setPhotoTagFilter] = useState<string | null>(null) // e.g. 'Confidential' to filter timeline
    const [photos, setPhotos] = useState<Photo[]>([])
    const [workOrders, setWorkOrders] = useState<WorkOrder[]>([])
    const [isLoading, setIsLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)
    const [refreshKey, setRefreshKey] = useState(0) // Key to trigger refetch

    // Camera capture flow state
    interface CapturedPhoto {
        file: File
        url: string // Blob URL for preview
        thumbnailUrl: string | null // Thumbnail URL for camera modal
        description?: string | null
        takenAt: string // ISO date when captured (for updatePhoto; canvas has no EXIF)
        tempId: string // Temporary ID to track this photo
        uploadedPhotoId?: number // ID of uploaded photo (if already uploaded)
    }
    
    const [isCameraOpen, setIsCameraOpen] = useState(false)
    const [capturedPhotos, setCapturedPhotos] = useState<CapturedPhoto[]>([])
    const [showPostCapturePopup, setShowPostCapturePopup] = useState(false)
    const [currentPhotoTempId, setCurrentPhotoTempId] = useState<string | null>(null) // tempId of photo currently getting description
    const [lastSavedThumbnailUrl, setLastSavedThumbnailUrl] = useState<string | null>(null) // Thumbnail URL that persists after upload
    
    // Description input state
    const [isVoiceRecordingOpen, setIsVoiceRecordingOpen] = useState(false)
    const [isTextInputOpen, setIsTextInputOpen] = useState(false)
    const [pendingTranscriptions, setPendingTranscriptions] = useState<Set<string>>(new Set()) // Track photos with pending transcriptions
    const [uploadingPhotos, setUploadingPhotos] = useState<Set<string>>(new Set()) // Track photos currently being uploaded (to prevent duplicates)
    const isTextInputSavingRef = useRef(false) // Track if text input is currently saving (prevents duplicate uploads)
    
    // Refs to track modal state for closure access in async functions
    const isCameraOpenRef = useRef(isCameraOpen)
    const isTextInputOpenRef = useRef(isTextInputOpen)
    const isVoiceRecordingOpenRef = useRef(isVoiceRecordingOpen)
    const currentTakenAtRef = useRef<string>(new Date().toISOString()) // takenAt from PostCapturePopup when user picks Voice/Text
    
    // Update refs when state changes
    useEffect(() => {
        isCameraOpenRef.current = isCameraOpen
    }, [isCameraOpen])
    
    useEffect(() => {
        isTextInputOpenRef.current = isTextInputOpen
    }, [isTextInputOpen])
    
    useEffect(() => {
        isVoiceRecordingOpenRef.current = isVoiceRecordingOpen
    }, [isVoiceRecordingOpen])

    /** Volunteer and TeamLeader may only edit/delete photos they uploaded. Admin and BaseLeader may edit any. */
    const canEditPhoto = (photo: Photo) => {
        if (!session) return false
        if (session.role === UserRole.ADMIN || session.role === UserRole.BASE_LEADER) return true
        if (session.role === UserRole.VOLUNTEER || session.role === UserRole.TEAM_LEADER) {
            const uploaderId = photo.uploadedByUser?.id ?? photo.uploadedByUserId
            return uploaderId != null && uploaderId === session.sub
        }
        return false
    }

    // Download option for Admin and Base Leader only
    const canDownloadPhotos =
        session?.role === UserRole.ADMIN || session?.role === UserRole.BASE_LEADER

    // Filter (All | Confidential) only visible to Admin, Base Leader, Team Leader
    const canShowFilter =
        session?.role === UserRole.ADMIN ||
        session?.role === UserRole.BASE_LEADER ||
        session?.role === UserRole.TEAM_LEADER

    // Fetch photos and work orders when homeId changes
    useEffect(() => {
        let isCancelled = false // Flag to prevent state updates if component unmounts or effect re-runs
        
        const fetchData = async () => {
            if (!homeId) {
                if (!isCancelled) setIsLoading(false)
                return
            }

            try {
                if (!isCancelled) setIsLoading(true)
                if (!isCancelled) setError(null)
                const homeIdNum = typeof homeId === 'string' ? parseInt(homeId, 10) : homeId
                
                if (isNaN(homeIdNum)) {
                    throw new Error('Invalid home ID')
                }

                // Timeout so we don't spin forever if the backend is down or unreachable
                const timeoutMs = 15000
                const timeoutPromise = new Promise<never>((_, reject) =>
                    setTimeout(
                        () =>
                            reject(
                                new Error(
                                    'Request timed out. Make sure the backend server is running (e.g. in the backend folder: pnpm run start:dev).'
                                )
                            ),
                        timeoutMs
                    )
                )

                // Fetch photos and work orders in parallel (with timeout)
                const [photosResponse, workOrdersResponse] = await Promise.race([
                    Promise.all([
                        getPhotosByHomeId(homeIdNum, {
                            includeDeleted: false,
                            ...(canShowFilter && photoTagFilter && { tags: [photoTagFilter] }),
                        }),
                        getAllWorkOrders({
                            homeId: homeIdNum, // Filter by homeId on the backend
                            limit: 1000, // Get a large number to ensure we get all work orders
                        }),
                    ]),
                    timeoutPromise,
                ])

                // Only update state if the effect hasn't been cancelled (component still mounted, effect hasn't re-run)
                if (!isCancelled) {
                    if (photosResponse.success && photosResponse.data) {
                        setPhotos(photosResponse.data)
                    } else {
                        setError('message' in photosResponse ? photosResponse.message : 'Failed to fetch photos')
                    }

                    if (workOrdersResponse.success && workOrdersResponse.data?.workOrders) {
                        // Work orders are already filtered by homeId on the backend
                        const workOrdersForHome = workOrdersResponse.data.workOrders
                        setWorkOrders(workOrdersForHome)
                    }
                }
            } catch (err) {
                if (!isCancelled) {
                    console.error('Error fetching data:', err)
                    setError(err instanceof Error ? err.message : 'Unknown error occurred')
                }
            } finally {
                if (!isCancelled) setIsLoading(false)
            }
        }

        fetchData()
        
        // Cleanup function to cancel the fetch if the effect re-runs or component unmounts
        return () => {
            isCancelled = true
        }
    }, [homeId, refreshKey, photoTagFilter, canShowFilter])

    // Function to refresh photos after upload
    const handleUploadSuccess = () => {
        setRefreshKey(prev => prev + 1) // Trigger refetch by changing the key
        setPreSelectedFiles([]) // Clear pre-selected files after successful upload
    }
    
    const handleUploadButtonClick = () => {
        // Trigger file picker directly
        fileInputRef.current?.click()
    }
    
    const handleFileSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files
        if (files && files.length > 0) {
            const fileArray = Array.from(files)
            setPreSelectedFiles(fileArray)
            setIsModalOpen(true) // Open modal after files are selected
        }
        // Reset input to allow selecting the same file again if needed
        event.target.value = ''
    }

    // Group photos and work orders by date into unified timeline
    const getTimelineDays = (): TimelineDay[] => {
        const timelineMap = new Map<string, { photos: Photo[]; workOrders: WorkOrder[] }>()

        // Group photos by date
        photos.forEach((photo) => {
            const dateToUse = photo.takenAt || photo.uploadedAt
            const date = new Date(dateToUse).toDateString()
            
            if (!timelineMap.has(date)) {
                timelineMap.set(date, { photos: [], workOrders: [] })
            }
            timelineMap.get(date)!.photos.push(photo)
        })

        // Group work orders by their actual created date (createdAt) so the timeline matches the Work Orders list
        workOrders.forEach((workOrder) => {
            const dateToUse = new Date(workOrder.createdAt)
            const date = dateToUse.toDateString()

            if (!timelineMap.has(date)) {
                timelineMap.set(date, { photos: [], workOrders: [] })
            }
            timelineMap.get(date)!.workOrders.push(workOrder)
        })

        // Convert to array and sort by date (most recent first)
        return Array.from(timelineMap.entries())
            .map(([date, data]) => ({
                date,
                displayDate: formatDisplayDate(date),
                photos: data.photos.sort((a, b) => {
                    const dateA = new Date(a.takenAt || a.uploadedAt)
                    const dateB = new Date(b.takenAt || b.uploadedAt)
                    return dateB.getTime() - dateA.getTime() // Most recent first
                }),
                workOrders: data.workOrders.sort((a, b) => {
                    const dateA = new Date(a.createdAt)
                    const dateB = new Date(b.createdAt)
                    return dateB.getTime() - dateA.getTime() // Most recent first
                }),
            }))
            .sort(
                (a, b) =>
                    new Date(b.date).getTime() - new Date(a.date).getTime()
            )
    }

    const formatDisplayDate = (dateString: string) => {
        const date = new Date(dateString)
        const today = new Date()
        const yesterday = new Date(today)
        yesterday.setDate(yesterday.getDate() - 1)

        if (date.toDateString() === today.toDateString()) {
            return "Today"
        } else if (date.toDateString() === yesterday.toDateString()) {
            return "Yesterday"
        } else {
            return date.toLocaleDateString("en-US", {
                weekday: "long",
                year: "numeric",
                month: "long",
                day: "numeric",
            })
        }
    }

    const formatDate = (dateString: string | null) => {
        if (!dateString) return 'Unknown date'
        return new Date(dateString).toLocaleDateString('en-US', {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        })
    }

    // Convert API Photo to DisplayPhoto for component use
    const convertPhotoToDisplay = (photo: Photo): DisplayPhoto => {
        return {
            id: photo.id.toString(),
            url: photo.imageUrl,
            thumbnailUrl: photo.thumbnailUrl,
            name: photo.description || `Photo ${photo.id}`,
            takenAt: photo.takenAt,
            uploadedAt: photo.uploadedAt,
            workOrderId: photo.workOrderId?.toString() || null,
            // Note: workOrderNumber would need to be fetched separately or included in API response
            workOrderNumber: null,
        }
    }

    // Handle escape key to close photo/video viewer
    useEffect(() => {
        const handleEscape = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                if (selectedPhoto) {
                    setSelectedPhoto(null)
                }
                if (selectedVideo) {
                    setSelectedVideo(null)
                }
            }
        }

        document.addEventListener('keydown', handleEscape)
        return () => document.removeEventListener('keydown', handleEscape)
    }, [selectedPhoto, selectedVideo])

    // Carousel: flat list of non-video photos for prev/next
    const carouselPhotos = photos.filter((p) => p.mediaType !== 'video')
    const carouselIndex = selectedPhoto
        ? carouselPhotos.findIndex((p) => p.id === selectedPhoto.id)
        : -1
    const hasPrev = carouselPhotos.length > 1 && carouselIndex > 0
    const hasNext = carouselPhotos.length > 1 && carouselIndex >= 0 && carouselIndex < carouselPhotos.length - 1

    const carouselPhotosRef = useRef<Photo[]>([])
    carouselPhotosRef.current = carouselPhotos

    // Arrow key navigation in photo viewer
    useEffect(() => {
        if (!selectedPhoto || selectedPhoto.mediaType === 'video') return
        const handleKey = (e: KeyboardEvent) => {
            const list = carouselPhotosRef.current
            const idx = list.findIndex((p) => p.id === selectedPhoto.id)
            if (e.key === 'ArrowLeft' && idx > 0) {
                e.preventDefault()
                setSelectedPhoto(list[idx - 1])
            }
            if (e.key === 'ArrowRight' && idx >= 0 && idx < list.length - 1) {
                e.preventDefault()
                setSelectedPhoto(list[idx + 1])
            }
        }
        document.addEventListener('keydown', handleKey)
        return () => document.removeEventListener('keydown', handleKey)
    }, [selectedPhoto])

    const handlePhotoClick = (photo: Photo) => {
        // Check if it's explicitly a video, otherwise treat as photo
        if (photo.mediaType === 'video') {
            setSelectedVideo(photo)
            setSelectedPhoto(null)
        } else {
            // For photos or undefined mediaType (legacy photos)
            setSelectedPhoto(photo)
            setSelectedVideo(null)
        }
    }

    const closePhotoViewer = () => {
        setSelectedPhoto(null)
        setPendingViewerDescription(undefined)
        setIsViewerVoiceOpen(false)
        setIsViewerTextOpen(false)
        setIsViewerTranscribing(false)
    }

    const closeVideoPlayer = () => {
        setSelectedVideo(null)
    }

    // Transcribe audio blob and return text (for viewer description from voice)
    const transcribeAudioBlob = async (
        audioBlob: Blob
    ): Promise<{ success: true; text: string } | { success: false; message: string }> => {
        const audioFile = new File([audioBlob], 'recording.webm', {
            type: audioBlob.type || 'audio/webm',
        })
        const formData = new FormData()
        formData.append('audio', audioFile)
        const response = await fetch('/api/ai/transcribe', {
            method: 'POST',
            body: formData,
            credentials: 'include',
        })
        const result = await response.json().catch(() => ({}))
        if (!response.ok) {
            const msg = result?.message || response.statusText || 'Transcription request failed'
            return { success: false, message: msg }
        }
        if (result.success && result.data?.transcription != null) {
            return { success: true, text: String(result.data.transcription) }
        }
        return {
            success: false,
            message: result?.message || 'No transcription returned',
        }
    }

    const handleViewerVoiceSave = async (audioBlob: Blob) => {
        setIsViewerVoiceOpen(false)
        if (!audioBlob || audioBlob.size < 100) {
            alert('No audio was recorded. Please try again and speak for at least a second before tapping Done.')
            return
        }
        if (!selectedPhoto) return
        setIsViewerTranscribing(true)
        try {
            const outcome = await transcribeAudioBlob(audioBlob)
            if (outcome.success) {
                const existing = (selectedPhoto.description ?? '').trim()
                const newDescription = existing ? `${existing} ${outcome.text}` : outcome.text
                setPendingViewerDescription(newDescription)
                const result = await updatePhoto(selectedPhoto.id, { description: newDescription })
                if (result.success) {
                    setSelectedPhoto((prev) => (prev ? { ...prev, description: newDescription } : null))
                    setPendingViewerDescription(undefined)
                    setRefreshKey((prev) => prev + 1)
                } else {
                    alert(`Failed to save description: ${result.message || 'Unknown error'}`)
                }
            } else {
                console.error('Viewer voice transcription failed:', outcome.message)
                alert(`Voice transcription failed: ${outcome.message}. Try again or use Keyboard to type a description.`)
            }
        } catch (error) {
            console.error('Error transcribing audio:', error)
            alert(
                `Voice transcription error: ${error instanceof Error ? error.message : 'Unknown error'}. Try Keyboard to type a description.`
            )
        } finally {
            setIsViewerTranscribing(false)
        }
    }

    const handleViewerTextSave = async (text: string) => {
        setIsViewerTextOpen(false)
        if (!selectedPhoto) return
        setPendingViewerDescription(text)
        try {
            const result = await updatePhoto(selectedPhoto.id, { description: text })
            if (result.success) {
                setSelectedPhoto((prev) => (prev ? { ...prev, description: text } : null))
                setPendingViewerDescription(undefined)
                setRefreshKey((prev) => prev + 1)
            } else {
                alert(`Failed to save description: ${result.message || 'Unknown error'}`)
            }
        } catch (error) {
            console.error('Error saving description:', error)
            alert('An error occurred while saving the description')
        }
    }

    const handleDeletePhoto = async (photoId: number, event: React.MouseEvent) => {
        event.stopPropagation() // Prevent photo click when clicking delete button
        
        if (!confirm('Are you sure you want to delete this photo?')) {
            return
        }

        try {
            const result = await deletePhoto(photoId)
            
            if (result.success) {
                setSelectedPhoto(null)
                setSelectedVideo(null)
                setPhotos(prev => prev.filter(p => p.id !== photoId))
            } else {
                alert(`Failed to delete photo: ${result.message || 'Unknown error'}`)
            }
        } catch (error) {
            console.error('Error deleting photo:', error)
            alert('An error occurred while deleting the photo')
        }
    }

    // Camera capture handlers
    const handleCameraFABClick = () => {
        setIsCameraOpen(true)
    }

    const handleCameraCapture = (file: File) => {
        console.log('📸 handleCameraCapture called:', {
            name: file.name,
            type: file.type,
            size: file.size,
            sizeMB: (file.size / 1024 / 1024).toFixed(2),
            lastModified: new Date(file.lastModified).toISOString()
        })
        
        // Verify file is not empty
        if (file.size === 0) {
            console.error('❌ ERROR: Received empty file!')
            alert('Error: The captured file is empty. Please try again.')
            return
        }
        
        // Create object URL for preview
        const url = URL.createObjectURL(file)
        console.log('🔗 Created blob URL:', url)
        const tempId = `photo-${Date.now()}-${Math.random()}`
        
        const isVideo = file.type.startsWith('video/')
        console.log(`📹 File type: ${isVideo ? 'VIDEO' : 'PHOTO'}`)
        
        // Description popup only for photos; videos upload immediately (they have their own audio)
        if (!isVideo) {
            setCurrentPhotoTempId(tempId)
        }
        
        if (isVideo) {
            console.log('🎬 Starting video thumbnail generation...')
            // Handle video thumbnail generation
            const video = document.createElement('video')
            video.src = url
            video.muted = true
            video.playsInline = true
            video.preload = 'metadata'
            
            console.log('📹 Video element created with blob URL:', {
                src: video.src,
                blobUrl: url,
                fileSize: file.size
            })
            
            let thumbnailGenerated = false
            let videoAdded = false
            
            // Timeout fallback - if thumbnail generation takes too long, add video anyway
            const timeoutId = setTimeout(() => {
                if (!videoAdded) {
                    console.warn('Video thumbnail generation timed out, adding video without thumbnail')
                    const newPhoto: CapturedPhoto = {
                        file,
                        url,
                        thumbnailUrl: null,
                        takenAt: new Date().toISOString(),
                        tempId,
                    }
                    setCapturedPhotos(prev => [...prev, newPhoto])
                    setLastSavedThumbnailUrl(null)
                    videoAdded = true
                    uploadPhotoImmediately(tempId, null, newPhoto).catch(err => {
                        console.error('❌ Error uploading video:', err)
                    })
                }
            }, 5000) // 5 second timeout
            
            const addVideoToArray = (thumbnailUrl: string | null) => {
                if (videoAdded) {
                    console.warn('⚠️ addVideoToArray called multiple times, skipping duplicate')
                    return // Prevent duplicate additions
                }
                videoAdded = true
                clearTimeout(timeoutId)
                
                const newPhoto: CapturedPhoto = {
                    file,
                    url,
                    thumbnailUrl,
                    takenAt: new Date().toISOString(),
                    tempId,
                }
                console.log('✅ Adding video to capturedPhotos array:', {
                    tempId,
                    fileName: file.name,
                    fileSize: file.size,
                    fileType: file.type,
                    hasThumbnail: !!thumbnailUrl,
                    blobUrl: url
                })
                setCapturedPhotos(prev => {
                    const newArray = [...prev, newPhoto]
                    console.log(`📋 capturedPhotos array updated: ${prev.length} -> ${newArray.length} items`)
                    return newArray
                })
                setLastSavedThumbnailUrl(null)
                // Videos skip description popup (they have their own audio) — upload immediately
                uploadPhotoImmediately(tempId, null, newPhoto).catch(err => {
                    console.error('❌ Error uploading video:', err)
                })
            }
            
            video.onloadedmetadata = () => {
                try {
                    // Seek to 1 second (or 10% of duration, whichever is smaller) for thumbnail
                    // For very short videos, use 0.1 seconds
                    const seekTime = video.duration > 0 
                        ? Math.min(1, Math.max(0.1, video.duration * 0.1))
                        : 0.1
                    video.currentTime = seekTime
                } catch (error) {
                    console.error('Error seeking video:', error)
                    // If seek fails, try to capture first frame
                    video.currentTime = 0
                }
            }
            
            video.onseeked = () => {
                if (thumbnailGenerated) return // Prevent duplicate thumbnail generation
                thumbnailGenerated = true
                
                try {
                    const canvas = document.createElement('canvas')
                    const size = 48 // Small thumbnail size
                    canvas.width = size
                    canvas.height = size
                    const ctx = canvas.getContext('2d')
                    if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
                        // Draw video frame centered and cropped to square
                        const scale = Math.max(size / video.videoWidth, size / video.videoHeight)
                        const x = (size - video.videoWidth * scale) / 2
                        const y = (size - video.videoHeight * scale) / 2
                        ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale)
                        canvas.toBlob((blob) => {
                            if (blob) {
                                const thumbnailUrl = URL.createObjectURL(blob)
                                addVideoToArray(thumbnailUrl)
                            } else {
                                console.warn('Failed to create thumbnail blob, adding video without thumbnail')
                                addVideoToArray(null)
                            }
                        }, 'image/jpeg', 0.8)
                    } else {
                        console.warn('Video dimensions not available, adding video without thumbnail')
                        addVideoToArray(null)
                    }
                } catch (error) {
                    console.error('Error generating video thumbnail:', error)
                    addVideoToArray(null)
                }
            }
            
            video.onerror = (error) => {
                console.error('❌ Error loading video for thumbnail:', {
                    error,
                    blobUrl: url,
                    fileSize: file.size,
                    fileType: file.type,
                    fileName: file.name,
                    videoError: video.error,
                    videoNetworkState: video.networkState,
                    videoReadyState: video.readyState
                })
                clearTimeout(timeoutId)
                // Fallback: add video without thumbnail
                addVideoToArray(null)
            }
            
            // Also handle case where video loads but metadata doesn't fire
            video.oncanplay = () => {
                if (!thumbnailGenerated && !videoAdded && video.readyState >= 2) {
                    // Try to generate thumbnail from current frame
                    try {
                        const canvas = document.createElement('canvas')
                        const size = 48
                        canvas.width = size
                        canvas.height = size
                        const ctx = canvas.getContext('2d')
                        if (ctx && video.videoWidth > 0 && video.videoHeight > 0) {
                            const scale = Math.max(size / video.videoWidth, size / video.videoHeight)
                            const x = (size - video.videoWidth * scale) / 2
                            const y = (size - video.videoHeight * scale) / 2
                            ctx.drawImage(video, x, y, video.videoWidth * scale, video.videoHeight * scale)
                            canvas.toBlob((blob) => {
                                if (blob && !videoAdded) {
                                    thumbnailGenerated = true
                                    const thumbnailUrl = URL.createObjectURL(blob)
                                    addVideoToArray(thumbnailUrl)
                                }
                            }, 'image/jpeg', 0.8)
                        }
                    } catch (error) {
                        console.error('Error in oncanplay thumbnail generation:', error)
                    }
                }
            }
            
            // Load video to trigger metadata load
            video.load()
        } else {
            // Handle photo: add to capturedPhotos and show description popup (PostCapturePopup)
            const addPhotoAndShowPopup = (thumbnailUrl: string | null) => {
                const newPhoto: CapturedPhoto = {
                    file,
                    url,
                    thumbnailUrl,
                    takenAt: new Date().toISOString(),
                    tempId,
                }
                setCapturedPhotos(prev => [...prev, newPhoto])
                setLastSavedThumbnailUrl(null)
                setShowPostCapturePopup(true)
            }

            const img = document.createElement('img')
            img.onerror = () => {
                // Image failed to load; still add photo and show description popup
                console.warn('Photo load failed for thumbnail, adding without thumbnail')
                addPhotoAndShowPopup(null)
            }
            img.onload = () => {
                const canvas = document.createElement('canvas')
                const size = 48
                canvas.width = size
                canvas.height = size
                const ctx = canvas.getContext('2d')
                if (!ctx) {
                    addPhotoAndShowPopup(null)
                    return
                }
                const scale = Math.max(size / img.width, size / img.height)
                const x = (size - img.width * scale) / 2
                const y = (size - img.height * scale) / 2
                ctx.drawImage(img, x, y, img.width * scale, img.height * scale)
                canvas.toBlob(
                    (blob) => {
                        if (blob) {
                            addPhotoAndShowPopup(URL.createObjectURL(blob))
                        } else {
                            addPhotoAndShowPopup(null)
                        }
                    },
                    'image/jpeg',
                    0.8
                )
            }
            img.src = url
        }
    }

    // Upload a single photo or video immediately (called when description is added or popup times out)
    // overridePhoto: when provided (e.g. for videos), use instead of lookup — avoids stale state when uploading right after add
    // takenAt: optional ISO string for canvas-captured photos (no EXIF); applied via updatePhoto after upload
    const uploadPhotoImmediately = async (
        photoTempId: string,
        description?: string | null,
        overridePhoto?: { file: File; url: string; thumbnailUrl: string | null; takenAt?: string; tempId: string },
        takenAt?: string | null
    ): Promise<number | undefined> => {
        const photo = overridePhoto ?? capturedPhotos.find(p => p.tempId === photoTempId)
        const takenAtToUse = takenAt ?? photo?.takenAt ?? overridePhoto?.takenAt
        if (!photo || !homeIdString) {
            console.error(`Photo/Video with tempId ${photoTempId} not found in capturedPhotos array`)
            if (!overridePhoto) console.error('Available tempIds:', capturedPhotos.map(p => p.tempId))
            return
        }
        
        const isVideo = photo.file.type.startsWith('video/')
        console.log(`📹 Uploading ${isVideo ? 'VIDEO' : 'PHOTO'}:`, {
            tempId: photoTempId,
            fileName: photo.file.name,
            fileType: photo.file.type,
            fileSize: photo.file.size,
            fileSizeMB: (photo.file.size / 1024 / 1024).toFixed(2) + ' MB',
            lastModified: new Date(photo.file.lastModified).toISOString()
        })
        
        // Verify file is still valid before upload
        if (photo.file.size === 0) {
            console.error('❌ ERROR: File size is 0, cannot upload!')
            throw new Error('File is empty and cannot be uploaded')
        }
        
        // For videos, verify the blob URL is still valid
        if (isVideo && photo.url) {
            console.log('🔍 Verifying video blob URL before upload:', {
                url: photo.url,
                urlType: photo.url.startsWith('blob:') ? 'blob' : 'other'
            })
        }
        
        // Check if this photo/video is already being uploaded to prevent duplicates
        if (uploadingPhotos.has(photoTempId)) {
            console.log(`Photo/Video ${photoTempId} is already being uploaded, skipping duplicate upload`)
            return undefined
        }
        
        // Mark as uploading
        setUploadingPhotos(prev => new Set(prev).add(photoTempId))

        try {
            const homeIdNum = parseInt(homeIdString, 10)
            if (isNaN(homeIdNum)) {
                throw new Error('Invalid home ID')
            }

            // Upload the photo/video (background operation - no UI refresh)
            console.log(`📤 Starting upload for ${isVideo ? 'video' : 'photo'}...`)
            console.log('📋 File details at upload time:', {
                name: photo.file.name,
                type: photo.file.type,
                size: photo.file.size,
                sizeMB: (photo.file.size / 1024 / 1024).toFixed(2),
                lastModified: new Date(photo.file.lastModified).toISOString()
            })
            
            // Create a new File object to ensure it's fresh (in case of blob URL issues)
            const fileToUpload = new File([photo.file], photo.file.name, {
                type: photo.file.type,
                lastModified: photo.file.lastModified
            })
            console.log('📁 Created fresh File object for upload:', {
                name: fileToUpload.name,
                size: fileToUpload.size,
                type: fileToUpload.type,
                sizeMatch: fileToUpload.size === photo.file.size
            })
            
            const result = await uploadMultiplePhotosDirect(homeIdNum, [fileToUpload])
            
            console.log(`📤 Upload result:`, {
                success: result.success,
                message: result.success ? undefined : result.message,
                dataLength: result.success ? result.data.length : 0
            })
            
            if (!result.success) {
                throw new Error(result.message || `Failed to upload ${isVideo ? 'video' : 'photo'}`)
            }
            
            if (result.data && result.data.length > 0) {
                const uploadedPhoto = result.data[0]
                console.log(`✅ ${isVideo ? 'Video' : 'Photo'} uploaded successfully:`, {
                    id: uploadedPhoto.id,
                    mediaType: uploadedPhoto.mediaType,
                    imageUrl: uploadedPhoto.imageUrl?.substring(0, 50) + '...'
                })
                
                // If there's a description or takenAt, update the photo/video (background operation)
                if (description || takenAtToUse) {
                    await updatePhoto(uploadedPhoto.id, {
                        ...(description && { description }),
                        ...(takenAtToUse && { takenAt: takenAtToUse }),
                    })
                }
                
                // Save thumbnail URL before removing photo (persists until new photo is taken)
                if (photo.thumbnailUrl) {
                    setLastSavedThumbnailUrl(photo.thumbnailUrl)
                }
                
                // Remove photo/video from array by tempId and clean up URLs (doesn't trigger photo list refresh)
                setCapturedPhotos(prev => {
                    const newArray = prev.filter(p => p.tempId !== photoTempId)
                    console.log(`📋 Removed ${isVideo ? 'video' : 'photo'} from capturedPhotos. Remaining:`, newArray.length)
                    return newArray
                })
                
                // Clean up blob URLs (but keep thumbnail URL for display)
                URL.revokeObjectURL(photo.url)
                // Don't revoke thumbnailUrl here - it's saved in lastSavedThumbnailUrl
                
                // Return the uploaded photo ID for use by transcription handlers
                return uploadedPhoto.id
            } else {
                throw new Error(`${isVideo ? 'Video' : 'Photo'} uploaded but no data returned`)
            }
        } catch (error) {
            console.error(`❌ Error uploading ${isVideo ? 'video' : 'photo'}:`, error)
            alert(error instanceof Error ? error.message : `Failed to upload ${isVideo ? 'video' : 'photo'}`)
            return undefined
        } finally {
            // Remove from uploading set
            setUploadingPhotos(prev => {
                const newSet = new Set(prev)
                newSet.delete(photoTempId)
                return newSet
            })
        }
    }

    const handlePostCaptureClose = (takenAt: string) => {
        setShowPostCapturePopup(false)
        
        // Only upload if popup closed due to timeout AND no modal is open AND not currently saving text input
        // Use refs for modal state so we're correct when called from a modal's onClose (state not yet updated)
        if (currentPhotoTempId !== null && !isVoiceRecordingOpenRef.current && !isTextInputOpenRef.current && !isTextInputSavingRef.current) {
            const photoTempId = currentPhotoTempId
            setCurrentPhotoTempId(null)
            // Upload in background (doesn't block UI or trigger refresh)
            uploadPhotoImmediately(photoTempId, null, undefined, takenAt).catch(error => {
                console.error('Background upload failed:', error)
            })
        }
        // Don't clear currentPhotoTempId if a modal is open - the modal needs it
    }
    
    // Clean up all photos and videos (called when camera closes)
    const cleanupAllPhotos = async () => {
        // Before closing, ensure any remaining photos/videos are uploaded immediately
        // This handles the case where user hits Done before the last photo's popup times out
        // Photos/videos with pending transcriptions will be uploaded without description, then updated later
        if (capturedPhotos.length > 0) {
            console.log(`📤 Camera closing - uploading ${capturedPhotos.length} remaining media file(s)...`)
            // Upload all remaining photos/videos immediately (without descriptions for pending transcriptions)
            // They will be updated with descriptions when transcription completes
            const uploadPromises = capturedPhotos.map(photo => {
                const isVideo = photo.file.type.startsWith('video/')
                console.log(`📤 Queuing upload for ${isVideo ? 'video' : 'photo'}:`, photo.file.name)
                // Upload photos/videos without descriptions (even if transcription is pending)
                // The transcription handler will update the photo when it completes
                return uploadPhotoImmediately(photo.tempId, null, undefined, photo.takenAt).catch(error => {
                    console.error(`❌ Background upload failed during cleanup for ${isVideo ? 'video' : 'photo'}:`, error)
                })
            })
            
            // Wait for uploads to complete (but don't wait for transcriptions)
            const results = await Promise.allSettled(uploadPromises)
            const successful = results.filter(r => r.status === 'fulfilled').length
            const failed = results.filter(r => r.status === 'rejected').length
            console.log(`✅ Cleanup complete: ${successful} successful, ${failed} failed`)
        }
        
        // Now clean up blob URLs
        capturedPhotos.forEach(photo => {
            URL.revokeObjectURL(photo.url)
            if (photo.thumbnailUrl) {
                URL.revokeObjectURL(photo.thumbnailUrl)
            }
        })
        // Revoke and clear the saved thumbnail when camera closes
        if (lastSavedThumbnailUrl) {
            URL.revokeObjectURL(lastSavedThumbnailUrl)
            setLastSavedThumbnailUrl(null)
        }
        setCapturedPhotos([])
        setCurrentPhotoTempId(null)
        setShowPostCapturePopup(false)
        // Refresh photos list now that camera is closed (photos are uploaded, transcriptions continue in background)
        setRefreshKey(prev => prev + 1)
    }

    const handlePostCaptureVoiceRecord = (takenAt: string) => {
        currentTakenAtRef.current = takenAt
        setShowPostCapturePopup(false)
        setIsVoiceRecordingOpen(true)
        // Don't clear currentPhotoTempId here - it will be cleared when save is complete
    }

    const handlePostCaptureTextInput = (takenAt: string) => {
        currentTakenAtRef.current = takenAt
        setShowPostCapturePopup(false)
        setIsTextInputOpen(true)
        // Don't clear currentPhotoTempId here - it will be cleared when save is complete
    }

    // Voice recording handler - uploads photo first, then updates with transcription when ready (background)
    const handleVoiceRecordingSave = async (audioBlob: Blob) => {
        setIsVoiceRecordingOpen(false)
        
        if (currentPhotoTempId === null) {
            console.error('No photo available for description')
            return
        }

        const photoTempId = currentPhotoTempId // Store tempId before clearing state
        
        // Clear state first so camera stays open
        setCurrentPhotoTempId(null)
        
        // Mark this photo as having a pending transcription
        setPendingTranscriptions(prev => new Set(prev).add(photoTempId))
        
        // Upload photo immediately without description (background operation)
        // Store the uploaded photo ID for later update
        let uploadedPhotoId: number | undefined
        try {
            uploadedPhotoId = await uploadPhotoImmediately(photoTempId, null, undefined, currentTakenAtRef.current)
            if (uploadedPhotoId) {
                // Store the uploaded photo ID in the captured photo (for transcription update)
                setCapturedPhotos(prev => prev.map(p => 
                    p.tempId === photoTempId ? { ...p, uploadedPhotoId } : p
                ))
            }
        } catch (error) {
            console.error('Error uploading photo:', error)
            // Remove from pending transcriptions if upload failed
            setPendingTranscriptions(prev => {
                const newSet = new Set(prev)
                newSet.delete(photoTempId)
                return newSet
            })
            return
        }
        
        // Now handle transcription in background
        (async () => {
            try {
                // Convert blob to File for FormData - webm format (most efficient for browsers)
                const audioFile = new File([audioBlob], 'recording.webm', { type: audioBlob.type || 'audio/webm' })
                
                // Send to transcription API
                const formData = new FormData()
                formData.append('audio', audioFile)

                const response = await fetch('/api/ai/transcribe', {
                    method: 'POST',
                    body: formData,
                })

                const result = await response.json()

                if (result.success && result.data?.transcription && uploadedPhotoId) {
                    // Update the already-uploaded photo with the transcription
                    await updatePhoto(uploadedPhotoId, { description: result.data.transcription })
                    // Only refresh photos list if camera is closed and no modals are open
                    // This prevents interrupting the user while they're typing descriptions
                    // Use refs to get current values (closure-safe)
                    if (!isCameraOpenRef.current && !isVoiceRecordingOpenRef.current && !isTextInputOpenRef.current) {
                        setRefreshKey(prev => prev + 1)
                    }
                } else {
                    // Transcription failed - photo is already uploaded without description
                    console.warn('Transcription failed:', result.message || 'Unknown error')
                }
            } catch (error) {
                // Transcription error - photo is already uploaded without description
                console.error('Error processing transcription:', error)
            } finally {
                // Remove from pending transcriptions
                setPendingTranscriptions(prev => {
                    const newSet = new Set(prev)
                    newSet.delete(photoTempId)
                    return newSet
                })
            }
        })()
    }

    // Text input handler - uploads photo immediately with text description (background)
    const handleTextInputSave = async (text: string) => {
        // Mark that we're saving to prevent handlePostCaptureClose from running
        isTextInputSavingRef.current = true
        
        setIsTextInputOpen(false)
        
        if (currentPhotoTempId === null) {
            console.error('No photo available for description')
            isTextInputSavingRef.current = false
            return
        }

        const photoTempId = currentPhotoTempId // Store tempId before clearing state
        
        // Clear state immediately to prevent handlePostCaptureClose from running
        setCurrentPhotoTempId(null)
        
        // Upload photo in background (doesn't block UI or trigger refresh)
        uploadPhotoImmediately(photoTempId, text, undefined, currentTakenAtRef.current)
            .catch(error => {
                console.error('Background upload failed:', error)
            })
            .finally(() => {
                // Reset the flag after upload completes
                isTextInputSavingRef.current = false
            })
    }

    const homeIdString = homeId ? String(homeId) : null
    
    if (!homeIdString) {
        return (
            <div className="text-center py-12 text-muted-foreground">
                <p>No home data available for this homeowner.</p>
            </div>
        )
    }

    const timelineDays = getTimelineDays()

    if (isLoading) {
        return (
            <div className="flex flex-col gap-6">
                <div className="flex justify-center md:justify-end items-center">
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={handleCameraFABClick}
                            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white"
                            size="sm"
                        >
                            <Camera className="w-3 h-3" />
                            Take
                        </Button>
                        <Button
                            onClick={handleUploadButtonClick}
                            className="flex items-center gap-2"
                            size="sm"
                        >
                            <PlusIcon className="w-3 h-3" />
                            Upload
                        </Button>
                    </div>
                </div>
                <div className="text-center py-12 text-muted-foreground">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
                    <p>Loading photos...</p>
                </div>
            </div>
        )
    }

    if (error) {
        return (
            <div className="flex flex-col gap-6">
                <div className="flex justify-center md:justify-end items-center">
                    <div className="flex items-center gap-2">
                        <Button
                            onClick={handleCameraFABClick}
                            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white"
                            size="sm"
                        >
                            <Camera className="w-3 h-3" />
                            Take
                        </Button>
                        <Button
                            onClick={handleUploadButtonClick}
                            className="flex items-center gap-2"
                            size="sm"
                        >
                            <PlusIcon className="w-3 h-3" />
                            Upload
                        </Button>
                    </div>
                </div>
                <div className="text-center py-12 text-red-500">
                    <p>Error loading photos: {error}</p>
                </div>
            </div>
        )
    }

    return (
        <>
            <div className="flex flex-col gap-6">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                    {canShowFilter ? (
                        <div className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground">Filter:</span>
                            <Button
                                type="button"
                                variant={photoTagFilter === null ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setPhotoTagFilter(null)}
                            >
                                All
                            </Button>
                            <Button
                                type="button"
                                variant={photoTagFilter === CONFIDENTIAL_TAG ? 'default' : 'outline'}
                                size="sm"
                                onClick={() => setPhotoTagFilter(photoTagFilter === CONFIDENTIAL_TAG ? null : CONFIDENTIAL_TAG)}
                            >
                                Confidential {photoTagFilter === CONFIDENTIAL_TAG ? 'On' : 'Off'}
                            </Button>
                        </div>
                    ) : (
                        <div />
                    )}
                    <div className="flex items-center gap-2 sm:justify-end">
                        <Button
                            onClick={handleCameraFABClick}
                            className="flex items-center gap-2 bg-primary hover:bg-primary/90 text-white"
                            size="sm"
                        >
                            <Camera className="w-3 h-3" />
                            Take
                        </Button>
                        <Button
                            onClick={handleUploadButtonClick}
                            className="flex items-center gap-2"
                            size="sm"
                        >
                            <PlusIcon className="w-3 h-3" />
                            Upload
                        </Button>
                    </div>
                </div>

                <div>
                    {/* Timeline with Photos and Work Orders */}
                    {timelineDays.length > 0 ? (
                        <div className="space-y-8">
                            {timelineDays.map((day) => (
                                <div key={day.date} className="space-y-4">
                                    {/* Date Header */}
                                    <div className="flex items-center gap-4">
                                        <h3 className="text-sm md:text-lg font-semibold text-foreground">
                                            {day.displayDate}
                                        </h3>
                                        <div className="flex-1 h-px bg-border"></div>
                                        <span className="text-sm text-muted-foreground">
                                            {day.photos.length} {day.photos.length !== 1 ? 'items' : 'item'}
                                        </span>
                                    </div>

                                    {/* Photos and Work Orders Section */}
                                    <div className="min-w-0">
                                        {(day.photos.length > 0 || day.workOrders.length > 0) ? (
                                            <div className="flex flex-wrap gap-2">
                                                {/* Photos and Videos */}
                                                {day.photos.map((photo) => {
                                                    const isVideo = photo.mediaType === 'video'
                                                    const isDocument = photo.mediaType === 'document'
                                                    
                                                    if (isVideo && !photo.thumbnailUrl) {
                                                        console.warn('Video photo missing thumbnailUrl:', photo)
                                                    }
                                                    
                                                    return (
                                                        <div
                                                            key={photo.id}
                                                            className="relative group bg-card rounded border overflow-hidden cursor-pointer hover:shadow-md transition-shadow inline-block"
                                                            onClick={() => handlePhotoClick(photo)}
                                                        >
                                                            {isVideo ? (
                                                                <VideoThumbnail
                                                                    photo={photo}
                                                                    onClick={() => handlePhotoClick(photo)}
                                                                    showDuration={true}
                                                                    showVideoIcon={true}
                                                                />
                                                            ) : isDocument ? (
                                                                <>
                                                                    <PdfThumbnail
                                                                        downloadApiPath={`/api/photos/${photo.id}/download`}
                                                                        className="object-cover rounded"
                                                                        style={{ height: '200px', width: 'auto' }}
                                                                        alt={photo.description || `PDF ${photo.id}`}
                                                                    >
                                                                        <div
                                                                            className="flex flex-col items-center justify-center rounded p-4 h-[200px] min-w-[140px] bg-muted"
                                                                            style={{ width: 'auto' }}
                                                                        >
                                                                            <FileText className="w-12 h-12 text-muted-foreground mb-2" />
                                                                            <span className="text-xs text-center text-muted-foreground">PDF</span>
                                                                        </div>
                                                                    </PdfThumbnail>
                                                                    <PhotoDescriptionIndicator
                                                                        hasDescription={!!photo.description}
                                                                    />
                                                                </>
                                                            ) : (
                                                                <>
                                                                    {/* eslint-disable-next-line @next/next/no-img-element */}
                                                                    <img
                                                                        src={photo.thumbnailUrl || photo.imageUrl}
                                                                        alt={photo.description || `Photo ${photo.id}`}
                                                                        className="object-cover rounded"
                                                                        style={{ height: '200px', width: 'auto' }}
                                                                    />
                                                                    <PhotoDescriptionIndicator
                                                                        hasDescription={!!photo.description}
                                                                    />
                                                                </>
                                                            )}
                                                        </div>
                                                    )
                                                })}
                                                {/* Work Orders - positioned like photos */}
                                                {day.workOrders.map((workOrder) => (
                                                    <WorkOrderTimelineCard
                                                        key={workOrder.id}
                                                        workOrder={workOrder}
                                                    />
                                                ))}
                                            </div>
                                        ) : (
                                            <div className="text-sm text-muted-foreground py-4">
                                                No photos on this date
                                            </div>
                                        )}
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="text-center py-12 text-muted-foreground">
                            <p>
                                No photos or work orders yet. Click &quot;Add
                                Photo&quot; to get started.
                            </p>
                        </div>
                    )}
                </div>
                <p className="text-xs text-muted-foreground rounded-md border border-border bg-muted/50 px-3 py-2 text-center">
                    {PHOTOS_SHARING_MESSAGE}
                </p>
            </div>

            {/* Hidden file input for direct file selection */}
            <input
                ref={fileInputRef}
                type="file"
                accept="image/*,video/*,.pdf,application/pdf"
                multiple
                onChange={handleFileSelection}
                className="hidden"
            />

            <AddPhotoModal
                isOpen={isModalOpen}
                onClose={() => {
                    setIsModalOpen(false)
                    setPreSelectedFiles([]) // Clear pre-selected files when modal closes
                }}
                homeId={homeIdString}
                onUploadSuccess={handleUploadSuccess}
                initialFiles={preSelectedFiles}
            />

            {/* Photo Viewer Modal */}
            {selectedPhoto && selectedPhoto.mediaType !== 'video' && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-90">
                    <div className="relative w-[700px] h-[80vh] mx-4">
                        {/* Photo Container */}
                        <div className="relative w-full h-full bg-white rounded-lg overflow-hidden flex flex-col">
                            {/* Photo or PDF takes available space */}
                            <div className="flex-1 relative min-h-0">
                                {/* Carousel arrows */}
                                {hasPrev && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute left-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white border-0"
                                        onClick={() => setSelectedPhoto(carouselPhotos[carouselIndex - 1])}
                                        aria-label="Previous photo"
                                    >
                                        <ChevronLeft className="h-6 w-6" />
                                    </Button>
                                )}
                                {hasNext && (
                                    <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="absolute right-2 top-1/2 -translate-y-1/2 z-10 h-10 w-10 rounded-full bg-black/50 hover:bg-black/70 text-white border-0"
                                        onClick={() => setSelectedPhoto(carouselPhotos[carouselIndex + 1])}
                                        aria-label="Next photo"
                                    >
                                        <ChevronRight className="h-6 w-6" />
                                    </Button>
                                )}
                                {selectedPhoto.mediaType === 'document' ? (
                                    <>
                                        <iframe
                                            src={selectedPhoto.imageUrl}
                                            title={selectedPhoto.description || `Document ${selectedPhoto.id}`}
                                            className="w-full h-full rounded"
                                        />
                                        <a
                                            href={selectedPhoto.imageUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="absolute bottom-2 left-2 text-xs text-primary underline hover:no-underline"
                                            onClick={(e) => e.stopPropagation()}
                                        >
                                            Open PDF in new tab
                                        </a>
                                    </>
                                ) : (
                                    <ZoomableImage
                                        src={selectedPhoto.imageUrl}
                                        alt={selectedPhoto.description || `Photo ${selectedPhoto.id}`}
                                        className="w-full h-full"
                                        showControls
                                    />
                                )}
                                {/* 3-line indicator when photo has a description (same as timeline thumbnails) */}
                                <div className="absolute bottom-2 right-2">
                                    <PhotoDescriptionIndicator
                                        hasDescription={
                                            ((pendingViewerDescription !== undefined ? pendingViewerDescription : selectedPhoto?.description)?.trim().length ?? 0) > 0
                                        }
                                    />
                                </div>
                            </div>

                            {/* Photo Info - always at bottom of container */}
                            <div className="bg-white text-gray-800 p-3 border-t flex-shrink-0">
                                {/* Photo Description - same formatting as pic 3 */}
                                {isViewerTranscribing ? (
                                    <div className="mb-3 pb-3 border-b">
                                        <p className="text-sm text-muted-foreground">Transcribing...</p>
                                    </div>
                                ) : (
                                    (() => {
                                        const displayDescription =
                                            pendingViewerDescription !== undefined
                                                ? pendingViewerDescription
                                                : selectedPhoto.description
                                        return displayDescription ? (
                                            <div className="mb-3 pb-3 border-b">
                                                <p className="text-sm text-gray-800 whitespace-pre-wrap">
                                                    {displayDescription}
                                                </p>
                                            </div>
                                        ) : null
                                    })()
                                )}
                                {/* Add/Edit description + Confidential tag: same line, tag right-justified */}
                                {canEditPhoto(selectedPhoto) && (
                                <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <span className="text-xs text-muted-foreground">Description:</span>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="gap-1.5"
                                            onClick={() => setIsViewerVoiceOpen(true)}
                                            aria-label="Record voice description"
                                        >
                                            <Mic className="w-4 h-4" />
                                            Voice
                                        </Button>
                                        <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            className="gap-1.5"
                                            onClick={() => setIsViewerTextOpen(true)}
                                            aria-label="Type description"
                                        >
                                            <Keyboard className="w-4 h-4" />
                                            Keyboard
                                        </Button>
                                    </div>
                                    <Button
                                        type="button"
                                        variant={(selectedPhoto.tags ?? []).includes(CONFIDENTIAL_TAG) ? 'default' : 'outline'}
                                        size="sm"
                                        className="gap-1.5 shrink-0"
                                        onClick={async () => {
                                            const current = selectedPhoto.tags ?? []
                                            const hasTag = current.includes(CONFIDENTIAL_TAG)
                                            const nextTags = hasTag
                                                ? current.filter((t) => t !== CONFIDENTIAL_TAG)
                                                : [...current, CONFIDENTIAL_TAG]
                                            try {
                                                const result = await updatePhoto(selectedPhoto.id, { tags: nextTags })
                                                if (result.success) {
                                                    setSelectedPhoto((prev) => (prev ? { ...prev, tags: nextTags } : null))
                                                    setRefreshKey((prev) => prev + 1)
                                                } else {
                                                    alert(`Failed to update tag: ${result.message || 'Unknown error'}`)
                                                }
                                            } catch (e) {
                                                console.error('Error updating photo tag:', e)
                                                alert('Failed to update tag')
                                            }
                                        }}
                                        aria-label={((selectedPhoto.tags ?? []).includes(CONFIDENTIAL_TAG) ? 'Remove' : 'Add') + ' Confidential tag'}
                                    >
                                        Confidential {(selectedPhoto.tags ?? []).includes(CONFIDENTIAL_TAG) ? 'On' : 'Off'}
                                    </Button>
                                </div>
                                )}
                                {/* Sticky bar: timestamp, WO link, Delete */}
                                <div className="flex justify-between items-center gap-2 flex-wrap">
                                    <div>
                                        <p className="text-xs text-gray-500">
                                            {selectedPhoto.uploadedByUser?.fullname && (
                                                <span className="block mb-0.5">
                                                    Added by {selectedPhoto.uploadedByUser.fullname}
                                                </span>
                                            )}
                                            {formatDate(selectedPhoto.takenAt || selectedPhoto.uploadedAt)}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-2">
                                        {selectedPhoto.workOrderId && (
                                            <Link
                                                href={`/work-orders/${selectedPhoto.workOrderId}`}
                                                target="_blank"
                                                className="inline-block"
                                            >
                                                <span className="inline-flex items-center px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full cursor-pointer hover:bg-blue-200 transition-colors">
                                                    WO-{selectedPhoto.workOrderId}
                                                </span>
                                            </Link>
                                        )}
                                    <div className="flex items-center gap-3">
                                        {canDownloadPhotos && (
                                            <Button
                                                variant="default"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={() =>
                                                    downloadPhotoOrVideo(selectedPhoto.imageUrl, {
                                                        id: selectedPhoto.id,
                                                        mediaType: (selectedPhoto.mediaType as 'image' | 'video' | 'document') ?? 'image',
                                                        downloadApiPath: `/api/photos/${selectedPhoto.id}/download`,
                                                    })
                                                }
                                                aria-label="Download"
                                            >
                                                <Download className="w-4 h-4" />
                                            </Button>
                                        )}
                                        {canEditPhoto(selectedPhoto) && (
                                            <Button
                                                variant="destructive"
                                                size="icon"
                                                className="h-8 w-8"
                                                onClick={async () => {
                                                    if (confirm('Are you sure you want to delete this photo?')) {
                                                        try {
                                                            const result = await deletePhoto(selectedPhoto.id)
                                                            if (result.success) {
                                                                const deletedId = selectedPhoto.id
                                                                closePhotoViewer()
                                                                setSelectedVideo(null)
                                                                setPhotos(prev => prev.filter(p => p.id !== deletedId))
                                                            } else {
                                                                alert(`Failed to delete photo: ${result.message || 'Unknown error'}`)
                                                            }
                                                        } catch (error) {
                                                            console.error('Error deleting photo:', error)
                                                            alert('An error occurred while deleting the photo')
                                                        }
                                                    }
                                                }}
                                            >
                                                <TrashIcon className="w-4 h-4" />
                                            </Button>
                                        )}
                                        <Button
                                            variant="outline"
                                            size="icon"
                                            className="h-8 w-8"
                                            onClick={closePhotoViewer}
                                            aria-label="Close"
                                        >
                                            <X className="w-4 h-4" />
                                        </Button>
                                    </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Viewer: Voice recording for description (uploaded photo) */}
            {selectedPhoto && isViewerVoiceOpen && (
                <VoiceRecordingModal
                    isOpen={isViewerVoiceOpen}
                    onClose={() => setIsViewerVoiceOpen(false)}
                    onSave={handleViewerVoiceSave}
                    autoStart={true}
                    imageUrl={selectedPhoto.imageUrl}
                />
            )}

            {/* Viewer: Text input for description (uploaded photo) */}
            {selectedPhoto && isViewerTextOpen && (
                <TextInputModal
                    isOpen={isViewerTextOpen}
                    onClose={() => setIsViewerTextOpen(false)}
                    onSave={handleViewerTextSave}
                    initialValue={
                        pendingViewerDescription !== undefined
                            ? pendingViewerDescription
                            : selectedPhoto.description ?? ''
                    }
                    imageUrl={selectedPhoto.imageUrl}
                />
            )}

            {/* Video Player Modal */}
            {selectedVideo && selectedVideo.mediaType === 'video' && (
                <VideoPlayer
                    photo={selectedVideo}
                    isOpen={true}
                    onClose={closeVideoPlayer}
                    onDelete={() => {
                        const deletedId = selectedVideo.id
                        setSelectedVideo(null)
                        setSelectedPhoto(null)
                        setPhotos(prev => prev.filter(p => p.id !== deletedId))
                    }}
                    canDelete={canEditPhoto(selectedVideo)}
                    canDownload={canDownloadPhotos}
                    downloadApiPath={`/api/photos/${selectedVideo.id}/download`}
                />
            )}

            {/* Camera Capture Modal */}
            <CameraCaptureModal
                isOpen={isCameraOpen}
                onClose={async () => {
                    // Ensure all photos are uploaded before closing
                    await cleanupAllPhotos()
                    setIsCameraOpen(false)
                }}
                onCapture={handleCameraCapture}
                lastCapturedThumbnail={
                    capturedPhotos.length > 0 
                        ? capturedPhotos[capturedPhotos.length - 1].thumbnailUrl 
                        : lastSavedThumbnailUrl
                }
                hideDone={showPostCapturePopup || isVoiceRecordingOpen || isTextInputOpen}
            />

            {/* Post-Capture Popup */}
            {showPostCapturePopup && currentPhotoTempId !== null && (() => {
                const currentPhoto = capturedPhotos.find(p => p.tempId === currentPhotoTempId)
                return currentPhoto ? (
                    <PostCapturePopup
                        imageUrl={currentPhoto.url}
                        initialTakenAt={currentPhoto.takenAt}
                        onTakenAtChange={(takenAt) => setCapturedPhotos(prev => prev.map(p => p.tempId === currentPhotoTempId ? { ...p, takenAt } : p))}
                        onVoiceRecord={handlePostCaptureVoiceRecord}
                        onTextInput={handlePostCaptureTextInput}
                        onClose={handlePostCaptureClose}
                    />
                ) : null
            })()}

            {/* Voice Recording Modal */}
            {isVoiceRecordingOpen && currentPhotoTempId !== null && (() => {
                const currentPhoto = capturedPhotos.find(p => p.tempId === currentPhotoTempId)
                return currentPhoto ? (
                    <VoiceRecordingModal
                        isOpen={isVoiceRecordingOpen}
                        onClose={() => {
                            setIsVoiceRecordingOpen(false)
                            isVoiceRecordingOpenRef.current = false
                            // If cancelled, upload photo without description (same as timeout)
                            if (currentPhotoTempId !== null) {
                                const p = capturedPhotos.find(x => x.tempId === currentPhotoTempId)
                                handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
                            }
                        }}
                        onSave={handleVoiceRecordingSave}
                        autoStart={true}
                        imageUrl={currentPhoto.url}
                    />
                ) : null
            })()}

            {/* Text Input Modal */}
            {isTextInputOpen && currentPhotoTempId !== null && (() => {
                const currentPhoto = capturedPhotos.find(p => p.tempId === currentPhotoTempId)
                return currentPhoto ? (
                    <TextInputModal
                        isOpen={isTextInputOpen}
                        onClose={() => {
                            // Only handle cancellation - not when saving
                            // Check if we're currently saving to prevent race conditions
                            if (!isTextInputSavingRef.current) {
                                setIsTextInputOpen(false)
                                isTextInputOpenRef.current = false
                                // If cancelled, upload photo without description (same as timeout)
                                if (currentPhotoTempId !== null) {
                                    const p = capturedPhotos.find(x => x.tempId === currentPhotoTempId)
                                    handlePostCaptureClose(p?.takenAt ?? new Date().toISOString())
                                }
                            }
                        }}
                        onSave={handleTextInputSave}
                        onSavingChange={(isSaving) => {
                            // Keep parent's ref in sync with modal's saving state
                            isTextInputSavingRef.current = isSaving
                        }}
                        imageUrl={currentPhoto.url}
                    />
                ) : null
            })()}

        </>
    )
}

